import { useEffect, useMemo, useRef } from "react";
import { cn } from "@/lib/utils";
import type { SupportTicketMessage } from "@/lib/api/support";
import { formatPerson } from "@/lib/api/support";

export interface ThreadActor {
  id: string;
  isStaff: boolean;
}

interface TicketThreadProps {
  messages: SupportTicketMessage[];
  /** Identity of the logged-in viewer; used to right-align their messages. */
  currentActor: ThreadActor;
  /** The patron who opened the ticket; used to label library-side replies. */
  patronUserId: string;
}

/* ── Design tokens kept local so the chat feel tunes in one place ─────── */
const BUBBLE_BASE = "max-w-[72%] px-4 py-2.5 text-sm leading-relaxed shadow-sm";
const BUBBLE_OWN = "bg-primary text-primary-foreground rounded-2xl rounded-br-md";
const BUBBLE_OTHER = "bg-slate-100 text-slate-900 rounded-2xl rounded-bl-md";
const AVATAR =
  "flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[11px] font-semibold text-slate-600";
const GAP_IN_GROUP = "mt-0.5";
const GAP_BETWEEN_GROUPS = "mt-3";
const GROUP_WINDOW_MS = 5 * 60 * 1000;

const dayLabelFmt = new Intl.DateTimeFormat(undefined, {
  weekday: "long",
  month: "short",
  day: "numeric",
  year: "numeric",
});
const timeFmt = new Intl.DateTimeFormat(undefined, { hour: "numeric", minute: "2-digit" });

/* ── Pure helpers ──────────────────────────────────────────────────────── */
function dayKey(d: Date): string {
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}
function dayLabel(d: Date): string {
  const today = new Date();
  const yest = new Date(today);
  yest.setDate(today.getDate() - 1);
  if (dayKey(d) === dayKey(today)) return "Today";
  if (dayKey(d) === dayKey(yest)) return "Yesterday";
  return dayLabelFmt.format(d);
}
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
}

interface Decorated {
  msg: SupportTicketMessage;
  isOwn: boolean;
  authorLabel: string;
  createdAt: Date;
  showDaySeparator: boolean;
  startsGroup: boolean;
}

function isMyMessage(msg: SupportTicketMessage, actor: ThreadActor): boolean {
  if (actor.isStaff) return msg.authorStaffId === actor.id;
  return msg.authorUserId === actor.id;
}

function isLibrarySide(msg: SupportTicketMessage, patronUserId: string): boolean {
  // Dedicated staff author is always library-side. A User-author is library-side
  // only when it's NOT the patron who opened the ticket (i.e. an admin/librarian
  // replying through their user-membership identity).
  if (msg.authorStaffId) return true;
  return !!msg.authorUserId && msg.authorUserId !== patronUserId;
}

function decorate(
  messages: SupportTicketMessage[],
  actor: ThreadActor,
  patronUserId: string
): Decorated[] {
  return messages.map((msg, i) => {
    const prev = messages[i - 1];
    const isOwn = isMyMessage(msg, actor);
    const fromLibrary = isLibrarySide(msg, patronUserId);
    const name = msg.authorStaffId ? formatPerson(msg.authorStaff) : formatPerson(msg.authorUser);
    const authorLabel = fromLibrary ? `${name} · Library` : name;
    const createdAt = new Date(msg.createdAt);
    const showDaySeparator = !prev || dayKey(createdAt) !== dayKey(new Date(prev.createdAt));
    const prevSameAuthor =
      !!prev && prev.authorStaffId === msg.authorStaffId && prev.authorUserId === msg.authorUserId;
    const withinWindow =
      !!prev && createdAt.getTime() - new Date(prev.createdAt).getTime() < GROUP_WINDOW_MS;
    const startsGroup = showDaySeparator || !prevSameAuthor || !withinWindow;
    return { msg, isOwn, authorLabel, createdAt, showDaySeparator, startsGroup };
  });
}

/* ── Component ─────────────────────────────────────────────────────────── */
export function TicketThread({ messages, currentActor, patronUserId }: TicketThreadProps) {
  const bottomRef = useRef<HTMLDivElement>(null);
  const items = useMemo(
    () => decorate(messages, currentActor, patronUserId),
    [messages, currentActor, patronUserId]
  );

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  return (
    <div className="flex flex-col py-2">
      {items.map((it) => (
        <div
          key={it.msg.id}
          className={cn("animate-fade-in-up", it.startsGroup ? GAP_BETWEEN_GROUPS : GAP_IN_GROUP)}
        >
          {it.showDaySeparator && (
            <div className="my-3 flex items-center gap-3">
              <div className="h-px flex-1 bg-slate-200" />
              <span className="text-xs font-medium text-slate-400">{dayLabel(it.createdAt)}</span>
              <div className="h-px flex-1 bg-slate-200" />
            </div>
          )}
          <div className={cn("flex items-end gap-2", it.isOwn ? "justify-end" : "justify-start")}>
            {!it.isOwn && (
              <div className={cn(AVATAR, !it.startsGroup && "invisible")} aria-hidden>
                {initials(it.authorLabel)}
              </div>
            )}
            <div className={cn("flex min-w-0 flex-col", it.isOwn ? "items-end" : "items-start")}>
              {it.startsGroup && (
                <div className="mb-0.5 px-1 text-[11px] font-medium text-slate-500">
                  {it.authorLabel}
                </div>
              )}
              <div className={cn(BUBBLE_BASE, it.isOwn ? BUBBLE_OWN : BUBBLE_OTHER)}>
                <div className="whitespace-pre-wrap break-words">{it.msg.body}</div>
              </div>
              <div className="mt-0.5 px-1 text-[10px] text-slate-400">
                {timeFmt.format(it.createdAt)}
              </div>
            </div>
          </div>
        </div>
      ))}
      <div ref={bottomRef} />
    </div>
  );
}
